import { format, startOfMonth, startOfWeek, endOfWeek, addDays, addWeeks, parseISO } from 'date-fns';
import { fromZonedTime, formatInTimeZone, toDate, toZonedTime } from 'date-fns-tz';

export type RepeatingAvailabilitySchedule = 'WEEKLY' | 'WEEKDAYS';

export enum AvailabilityEditScope {
  SINGLE = 'single',
  ALL_RECURRING = 'all_recurring',
}

export enum AvailabilityPendingAction {
  UPDATE = 'update',
  DELETE = 'delete',
}

export const DEFAULT_HOME_TIME_ZONE = 'America/Chicago';

export const REPEATING_AVAILABILITY_OVERLAP_ERROR = 'repeating_availability_overlap';

/** Prefer GraphQL validation messages for known availability errors; otherwise fallback. */
export function availabilityMutationErrorMessage(error: unknown, fallback: string): string {
  const graphQLError = (error as { graphQLErrors?: Array<{ message?: string; extensions?: { error_code?: string } }> })
    ?.graphQLErrors?.[0];
  if (graphQLError?.extensions?.error_code === REPEATING_AVAILABILITY_OVERLAP_ERROR && graphQLError.message) {
    return graphQLError.message;
  }
  return fallback;
}

/** Treat date Y/M/D/H/M/S as wall clock in timeZone and return a UTC ISO string. */
export function wallClockToUtcIso(date: Date, timeZone: string): string {
  return fromZonedTime(date, timeZone).toISOString();
}

export function formatInHomeTimeZone(iso: string, timeZone: string, pattern: string): string {
  return formatInTimeZone(parseISO(iso), timeZone, pattern);
}

/** Matches empire-graphql createRepeatingAvailability default materialization horizons. */
export function defaultRepeatingEffectiveUntil(start: Date, schedule: RepeatingAvailabilitySchedule): Date {
  return schedule === 'WEEKLY' ? addWeeks(start, 12) : addDays(start, 89);
}

export function monthKey(d: Date): string {
  return format(startOfMonth(d), 'yyyy-MM-dd');
}

export function weekKey(d: Date): string {
  return format(startOfWeek(d), 'yyyy-MM-dd');
}

export function dayKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

export function generateMonths(earliest: Date, latest: Date): any {
  // generate new collections for each week
  const months = {};
  let weekStart = startOfWeek(earliest);

  while (weekStart < latest) {
    const mk = monthKey(weekStart);
    const wk = weekKey(weekStart);

    // only generate collections in the present/future, not the past
    if (weekStart.getMonth() >= earliest.getMonth() || weekStart.getFullYear() > earliest.getFullYear()) {
      if (!months[mk]) months[mk] = {};
      months[mk][wk] = {};
    }

    const weekEnd = endOfWeek(weekStart);

    // generate the same week in both months
    // availabilities will appear in each one depending on their month
    if (weekStart.getMonth() !== weekEnd.getMonth()) {
      const k = monthKey(weekEnd);
      if (!months[k]) months[k] = {};
      months[k][wk] = {};
    }

    weekStart = addDays(weekStart, 7);
  }

  return months;
}

/** Whether to render a week-range subheading under a month section.
 * True when the week starts after today (skip the current/past week) and the week’s
 * start date falls in that month (cross-month weeks are only labeled in the month they begin). */
export function showWeekHeader(monthStart: string, weekStart: string, timeZone: string = DEFAULT_HOME_TIME_ZONE): boolean {
  const today = dayKey(toZonedTime(new Date(), timeZone));
  const weekIsFuture = weekStart > today;
  const weekStartsInThisMonth = weekStart.slice(0, 7) === monthStart.slice(0, 7); // yyyy-MM
  return weekIsFuture && weekStartsInThisMonth;
}

export function monthHeader(monthStart: string): string {
  return format(toDate(monthStart), 'MMMM yyyy');
}

export function weekHeader(weekStart: string): string {
  const start = toDate(weekStart);
  const end = endOfWeek(start);
  return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
}
